import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

export default function OAuthCallbackPage() {
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setToken } = useAuth();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get OAuth parameters from URL
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      // Debug logging
      console.log('OAuth Callback - URL params:', { code: !!code, state: !!state });
      console.log('OAuth Callback - Full URL:', window.location.href);

      if (!code) {
        throw new Error('Missing authorization code from Bluesky. Please try logging in again.');
      }

      if (!state) {
        throw new Error('Missing state parameter from Bluesky. Please try logging in again.');
      }

      // Get stored state from sessionStorage
      const storedState = sessionStorage.getItem('oauth_state');

      console.log('OAuth Callback - Stored state found:', !!storedState);

      if (!storedState) {
        throw new Error('OAuth state expired or not found. Please try logging in again.');
      }

      // Clear the stored state
      sessionStorage.removeItem('oauth_state');

      console.log('OAuth Callback - Calling backend API...');

      // Call backend callback endpoint
      const response = await fetch(
        `/api/auth/bluesky/callback?code=${encodeURIComponent(
          code
        )}&state=${encodeURIComponent(state)}&stored_state=${encodeURIComponent(storedState)}`
      );

      console.log('OAuth Callback - Response status:', response.status);

      if (!response.ok) {
        // Try to parse error response
        const errorData = await response.json().catch(() => null);
        console.error('OAuth Callback - Error response:', errorData);

        const errorMsg = errorData?.description || errorData?.details || errorData?.error || 'Token exchange failed';
        const fullError = errorData
          ? `Error: ${errorData.error || 'unknown'}\nDetails: ${errorData.details || 'N/A'}\nDescription: ${errorData.description || 'N/A'}`
          : errorMsg;

        throw new Error(fullError);
      }

      // Parse the JSON response
      const data = await response.json();
      console.log('OAuth Callback - Success response:', data);

      if (data.token) {
        // Use AuthContext to set token (will auto-fetch user)
        setToken(data.token);
        console.log('OAuth Callback - Token set, navigating to home');
        navigate('/');
        return;
      }

      // If we get here, something unexpected happened
      throw new Error('Unexpected response from server - no token in response');
    } catch (err: any) {
      console.error('OAuth callback error:', err);
      setStatus('error');
      setError(err.message || 'OAuth authentication failed');

      // Redirect to login after 10 seconds (give time to read error details)
      setTimeout(() => {
        navigate('/login');
      }, 10000);
    }
  };

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="mb-4 text-center">
            <svg
              className="w-16 h-16 text-red-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4 text-center">
            Authentication Failed
          </h1>
          <div className="bg-gray-900 rounded p-4 mb-4">
            <pre className="text-red-400 text-sm whitespace-pre-wrap font-mono">
              {error}
            </pre>
          </div>
          <p className="text-sm text-gray-500 text-center">
            Redirecting to login page in 10 seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8 text-center">
        <div className="mb-4">
          <svg
            className="animate-spin h-16 w-16 text-blue-500 mx-auto"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Completing Authentication</h1>
        <p className="text-gray-400">
          Exchanging authorization code for access token...
        </p>
      </div>
    </div>
  );
}
